import React, { useEffect, useState, useCallback } from 'react'
import Container from '../../Components/Container'
import toast, { Toaster } from 'react-hot-toast'
import { FaCalendar, FaShoppingBasket, FaTrash, FaBarcode, FaClock } from 'react-icons/fa'

const readJson = async (res) => {
    if (!res.ok) throw new Error('Sunucu yanıt vermedi')
    return res.json()
}

/** sales.php selectList yanıtı: dizi, [dizi] veya { data: [] } olabilir */
const normalizeListRows = (payload) => {
    if (payload == null) return []
    if (Array.isArray(payload)) {
        if (payload.length > 0 && Array.isArray(payload[0])) return payload[0]
        return payload
    }
    if (typeof payload === 'object' && Array.isArray(payload.data)) return payload.data
    return []
}

const toDisplayDate = (isoYmd) => {
    if (!isoYmd || typeof isoYmd !== 'string') return '—'
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoYmd.trim())
    if (!m) return isoYmd
    const y = Number(m[1])
    const mo = Number(m[2])
    const d = Number(m[3])
    return new Date(y, mo - 1, d).toLocaleDateString('tr-TR')
}

const todayIso = () => new Date().toISOString().split('T')[0]

const ProductThumb = ({ src, alt, className }) => {
    const [broken, setBroken] = useState(!src)
    if (broken) {
        return (
            <div
                className={`flex items-center justify-center bg-gray-100 text-gray-400 text-xs ${className}`}
                aria-hidden
            >
                —
            </div>
        )
    }
    return (
        <img
            src={src}
            alt={alt || ''}
            className={className}
            onError={() => setBroken(true)}
        />
    )
}

const List = () => {
    const [data, setData] = useState([])
    const [date, setDate] = useState(todayIso)

    const fetchData = useCallback((selectedDate) => {
        const formdata = new FormData()
        formdata.append('action', 'selectList')
        formdata.append('date', selectedDate)

        fetch(process.env.REACT_APP_BASE_URL + 'sales.php', {
            method: 'POST',
            body: formdata,
        })
            .then(readJson)
            .then((json) => {
                setData(normalizeListRows(json))
            })
            .catch((err) => {
                console.error(err)
                toast.error('Veri yüklenirken bir hata oluştu')
                setData([])
            })
    }, [])

    const deleteHandel = (sno) => {
        if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) return

        toast.promise(
            new Promise((resolve, reject) => {
                const formdata = new FormData()
                formdata.append('action', 'deleteList')
                formdata.append('sno', sno)

                fetch(process.env.REACT_APP_BASE_URL + 'sales.php', {
                    method: 'POST',
                    body: formdata,
                })
                    .then(readJson)
                    .then((resData) => {
                        if (Number(resData.status) === 200) {
                            resolve(resData)
                            fetchData(date)
                        } else {
                            reject(new Error(resData.message || 'Silinemedi'))
                        }
                    })
                    .catch((err) => {
                        console.error(err)
                        reject(err instanceof Error ? err : new Error('Silme işlemi başarısız'))
                    })
            }),
            {
                loading: 'Siliniyor...',
                success: 'Başarıyla silindi',
                error: (e) => (e && e.message) || 'Silme işlemi başarısız',
            }
        )
    }

    useEffect(() => {
        fetchData(date)
    }, [date, fetchData])

    const displayDate = toDisplayDate(date)
    const isEmpty = data.length === 0

    return (
        <Container padding bg="#f8fafc">
            <Toaster position="top-right" />

            <div className="mx-auto w-full max-w-6xl space-y-6">
                <div className="w-full rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
                        <div className="flex items-center gap-3">
                            <FaShoppingBasket className="text-indigo-600" size={24} />
                            <div>
                                <h1 className="text-2xl font-bold text-gray-800">Alınacaklar Listesi</h1>
                                <p className="text-sm text-gray-500">Alınması gereken ürünlerin listesi</p>
                            </div>
                        </div>

                        <div className="flex flex-col items-end gap-2">
                            <div className="flex items-center gap-2 text-gray-500">
                                <FaCalendar size={16} />
                                <span className="text-sm">{displayDate}</span>
                            </div>
                            <input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                    </div>
                </div>

                {isEmpty ? (
                    <div className="w-full rounded-xl border border-dashed border-gray-200 bg-white py-16 text-center shadow-sm">
                        <FaShoppingBasket className="mx-auto mb-4 text-gray-400" size={48} />
                        <h3 className="text-lg font-medium text-gray-900">Liste boş</h3>
                        <p className="mt-2 text-sm text-gray-500">Bu tarih için alınacak ürün yok.</p>
                    </div>
                ) : (
                    <>
                        <div className="hidden w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm lg:block">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-gray-200 bg-gray-50">
                                            <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                                                Sıra No
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                                                Ürün
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                                                Ürün Adı
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                                                Barkod
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                                                Tarih
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium uppercase text-gray-500">
                                                İşlem
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {data.map((item) => (
                                            <tr key={item.sno} className="hover:bg-gray-50">
                                                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                                    {item.sno}
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4">
                                                    <ProductThumb
                                                        src={item.img}
                                                        alt={item.name}
                                                        className="h-12 w-12 rounded-lg object-cover"
                                                    />
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                                                    {item.name}
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                                    {item.barcode}
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                                    {item.date}
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4 text-right">
                                                    <button
                                                        type="button"
                                                        onClick={() => deleteHandel(item.sno)}
                                                        className="inline-flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-red-600 transition-colors hover:bg-red-100"
                                                    >
                                                        <FaTrash size={14} />
                                                        <span>Sil</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="w-full space-y-4 lg:hidden">
                            {data.map((item) => (
                                <div
                                    key={item.sno}
                                    className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
                                >
                                    <div className="relative aspect-video">
                                        <ProductThumb
                                            src={item.img}
                                            alt={item.name}
                                            className="absolute inset-0 h-full w-full object-cover"
                                        />
                                    </div>
                                    <div className="space-y-3 p-4">
                                        <h3 className="font-medium text-gray-900">{item.name}</h3>

                                        <div className="flex items-center gap-2 text-sm text-gray-500">
                                            <FaBarcode className="text-gray-400" />
                                            <span>{item.barcode}</span>
                                        </div>

                                        <div className="flex items-center gap-2 text-sm text-gray-500">
                                            <FaClock className="text-gray-400" />
                                            <span>{item.date}</span>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => deleteHandel(item.sno)}
                                            className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-50 px-4 py-2 text-red-600 transition-colors hover:bg-red-100"
                                        >
                                            <FaTrash size={14} />
                                            <span>Sil</span>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </Container>
    )
}

export default List
